import { TranslateService } from '@ngx-translate/core';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { ActivatedRoute, Router } from '@angular/router';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { environment } from '../../../environments/environment';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { isPanicActive, sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';
import { MenuPage } from '../menu/menu.page';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import Address from '../../../smoothr-web-app-core/models/Address';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationService } from 'src/services/navigation.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaymentSuccessPage } from '../payment-success/payment-success.page';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import Table from 'src/smoothr-web-app-core/models/Table';
import { catchError, map, of, switchMap, timer } from 'rxjs';
import { PanicModeModalComponent } from 'src/app/components/panic-mode-modal/panic-mode-modalcomponent';
import { ModalController } from '@ionic/angular';
import { AllergensInfoModalComponent } from 'src/app/components/allergens-info-modal/allergens-info-modal.component';

enum Language {
	English = 'en',
	German = 'de',
	French = 'fr',
	Spanish = 'es',
	Portugal = 'pt',
	Italy = 'it',
	Norway = 'no',
}

export enum HomeFormKeys {
	TABLE_NUMBER = 'tableNumber',
	TABLE_PROVIDED = 'tableProvided',
}

export type HomeForm = {
	[HomeFormKeys.TABLE_NUMBER]: FormControl<string>;
	[HomeFormKeys.TABLE_PROVIDED]: FormControl<boolean>;
};

@Component({
	selector: 'app-home',
	templateUrl: './home.page.html',
	styleUrls: ['./home.page.scss'],
	standalone: false,
})
export class HomePage extends RepositoryDirective implements OnInit {
	private router = inject(Router);
	public translate = inject(TranslateService);
	private route = inject(ActivatedRoute);
	private snackBar = inject(MatSnackBar);
	private navigationService = inject(NavigationService);
	protected repository: RepositoryService = inject(RepositoryService);
	private fb = inject(FormBuilder);
	private modalCtrl = inject(ModalController);

	static url = 'home';
	static scanUrlWithTable = 'scan/:venueId/:tableNumber';
	static scanUrlWithoutTable = 'scan/:venueId';

	environment = environment;
	languageEnum = Language;

	loading = false;
	preorderTypes = [PreorderType.INSIDE, PreorderType.TAKE_AWAY];
	active: any = {};
	currentLang = signal(this.translate.currentLang);
	candidateVenue: Venue | null = null;

	protected readonly HomeFormKeys = HomeFormKeys;
	public homeForm: FormGroup<HomeForm>;

	public showInputsBlock = false;
	private table: Table | null = null;

	constructor() {
		super();
	}

	static async navigate(router: Router, repository?: RepositoryService) {
		await router.navigateByUrl(HomePage.url);
		if (repository) {
			repository.resetVenue();
			repository.address.emit(null);
			repository.order.emit(null);
			repository.verifiedOrder.emit(null);
			repository.payment.emit(null);
		}
	}

	ngOnInit() {
		super.ngOnInit();
		this.loading = true;
		this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
			console.log(params, params.get('venueId'), params.get('tableNumber'));
			if (params && params.get('venueId')) {
				this.loadVenueByQrCode(params.get('venueId'), params.get('tableNumber'));
			} else {
				this.showInputsBlock = true;
				this.loadVenues();
			}
		});

		this.createHomeForm();
	}

	ionViewDidEnter() {
		this.repository.order.emit(null);
		this.repository.verifiedOrder.emit(null);
	}

	async locateAndShowStores(preorderType: PreorderType) {
		if (this.loading) {
			return;
		}
		this.loading = true;

		localStorage.setItem(PaymentSuccessPage.notification_key, JSON.stringify(false));
		if (!this.candidateVenue) {
			console.log('No available venues');
			this.loading = false;
			return;
		}

		if (isPanicActive(this.candidateVenue)) {
			PanicModeModalComponent.show(this.modalCtrl);
			this.loading = false;

			return;
		}

		await this.loadVenueAndCreateOrder(this.candidateVenue, null, preorderType);
	}

	async loadVenueAndCreateOrder(venue: Venue, address: Address, preorderType: PreorderType) {
		const loadedVenue = await this.repository.getVenue(venue._id);

		this.repository.createOrder(loadedVenue, address, preorderType, null);
		if (this.table && !this.homeForm.getRawValue().tableProvided) {
			this.order.tableNumber = this.table.number;
			this.order.table = this.table._id;
			this.repository.order.emit(this.order);
		} else {
			this.order.tableNumber = null;
			this.order.table = null;
			this.repository.order.emit(this.order);
		}

		console.log(this.homeForm.getRawValue().tableProvided, this.table);
		console.log(this.homeForm.getRawValue().tableProvided || this.table);

		if (this.homeForm.getRawValue().tableProvided || this.table) {
			await this.navigationService.navigateSelectedCategory();
			this.loading = false;
		} else {
			this.snackBar.open(this.translate.instant('home_page.provide_table_number_error'), null, {
				duration: 3000,
			});
			this.loading = false;
		}
		return;
	}

	loadVenues() {
		for (const preorderType of this.preorderTypes) {
			this.active[preorderType] = false;
		}
		new Promise<void>(async (resolve, reject) => {
			try {
				const venues = (await this.repository.getAllVenues()).filter(this.venueFilterByURL.call(this));

				console.log(venues);

				for (const venue of venues) {
					for (const preorderType of this.preorderTypes) {
						this.active[preorderType] = this.active[preorderType] || venueAcceptsOrders(venue, preorderType);
					}
				}
				this.candidateVenue = venues[0];
				if (isPanicActive(this.candidateVenue)) {
					PanicModeModalComponent.show(this.modalCtrl);
				}
				resolve();
			} catch (e) {
				reject(e);
			}
		}).finally(() => (this.loading = false));
	}
	async goToMenu(type: PreorderType) {
		this.locateAndShowStores(type);
	}

	allergensAction() {
		// if (this.candidateVenue && this.candidateVenue.nutritionTable) {
		// 	window.open(this.candidateVenue.nutritionTable, '_blank');
		// }

		AllergensInfoModalComponent.show(this.modalCtrl);
	}

	changeLang(language: string) {
		this.translate.setDefaultLang(language);
		this.translate.use(language);
		this.currentLang.set(language);
	}
	async loadVenueByQrCode(venueId: string, tableNumber?: string) {
		this.loading = true;
		try {
			const venueData = await this.repository.getVenue(venueId);
			if (isPanicActive(venueData)) {
				PanicModeModalComponent.show(this.modalCtrl);
			}
			let table = null;
			if (tableNumber) {
				table = (await Api.getTable(venueData._id, tableNumber)).data as any;
			}
			await sleep(100);

			if (!table || !table?.active) {
				this.showInputsBlock = true;
			} else {
				this.table = table;
				this.showInputsBlock = false;
			}
			if (venueData) {
				this.candidateVenue = venueData;
				// this.order.tableNumber = table.number;
				// this.order.table = table._id;
				// this.repository.order.emit(this.order);
				sleep(200);
				// await MenuPage.navigate(this.router);
			}
		} catch (e) {
			this.snackBar.open(this.translate.instant('home_page.scan_table_error'), null, {
				duration: 2000,
			});
		} finally {
			this.loading = false;
		}
	}

	createHomeForm() {
		const formControls: HomeForm = {
			[HomeFormKeys.TABLE_NUMBER]: this.fb.control('', {
				validators: [Validators.required],
				asyncValidators: [this.tableNumberValidator()],
				updateOn: 'change',
			}),
			[HomeFormKeys.TABLE_PROVIDED]: this.fb.control(false),
		};
		this.homeForm = this.fb.group(formControls);

		this.homeForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
			console.log(value);
		});
	}

	updateProvideTableNumber() {
		this.homeForm.patchValue({
			tableProvided: !this.homeForm.getRawValue().tableProvided,
		});
	}

	private tableNumberValidator(): AsyncValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value) {
				return of(null);
			}
			if (!this.candidateVenue?._id) {
				return of(null);
			}

			return timer(400).pipe(
				switchMap(() => Api.getTable(this.candidateVenue?._id, control.value)),
				map(table => {
					if (table.data?.active) {
						this.table = table.data;
						return null;
					} else {
						this.table = null;
						return { tableNotFound: true };
					}
				}),
				takeUntilDestroyed(this.destroyRef),
				catchError(() => {
					this.table = null;
					return of({ tableNotFound: true });
				})
			);
		};
	}

	venueFilterByURL(): Function {
		return (venue: Venue) =>
			environment.production ? venue.readableId === 'tigerstaden_ssp' : venue.readableId === 'Tigerstaden_dev1';
	}
}
